/**
 * MobileAppBar — native-style top app bar shown ONLY below `lg`.
 *
 * Replaces the desktop TopBar (which reflows awkwardly on phones) with the kit's
 * mobile chrome: hamburger (opens the full sidebar drawer) · centered screen
 * title · notification bell · account. The brand mark sits in the sidebar
 * drawer, not beside the hamburger. Respects the top safe-area inset so it
 * clears the status bar / notch on native.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, User } from 'lucide-react';
import { NotificationBell } from './NotificationBell';

interface MobileAppBarProps {
  onToggleSidebar: () => void;
}

// Longest / most specific prefixes first.
const TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/marketing/social', 'Social Automator'],
  ['/calendar', 'Calendar'],
  ['/settings/integrations', 'Integrations'],
  ['/knowledge-base', 'Knowledge Base'],
  ['/privacy-center', 'Privacy Center'],
  ['/business', 'Business Hub'],
  ['/studio', 'Automate'],
  ['/automations', 'Automate'],
  ['/chat', 'Workspace'],
  ['/inbox', 'Inbox'],
  ['/popups', 'Popups'],
  ['/account', 'Account'],
];

function titleFor(pathname: string): string {
  const match = TITLES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`));
  return match ? match[1] : 'BuzzBit X';
}

export const MobileAppBar: React.FC<MobileAppBarProps> = ({ onToggleSidebar }) => {
  const { pathname } = useLocation();
  const title = titleFor(pathname);

  return (
    <header className="lg:hidden relative flex items-center justify-between h-14 px-2 bg-white border-b border-gray-200 app-bar-safe">
      <div className="flex items-center">
        <button
          onClick={onToggleSidebar}
          aria-label="Open menu"
          className="p-2 -ml-0.5 rounded-lg text-gray-800 active:bg-gray-100 transition-colors"
        >
          <Menu className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      <h1 className="absolute left-1/2 -translate-x-1/2 max-w-[55%] truncate text-center text-base font-semibold text-gray-900">
        {title}
      </h1>

      <div className="flex items-center gap-0.5">
        <NotificationBell />
        <Link
          to="/account"
          aria-label="Account"
          className="p-2 rounded-lg text-gray-800 active:bg-gray-100 transition-colors"
        >
          <User className="w-6 h-6" aria-hidden="true" />
        </Link>
      </div>
    </header>
  );
};

export default MobileAppBar;
